import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle2, 
  Clock, 
  Users, 
  Video, 
  Globe, 
  Sparkles,
  ArrowRight,
  HelpCircle,
  ExternalLink
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const AltspaceVR = () => {
  const faqData = [
    {
      question: "What happened to AltspaceVR?",
      answer: "AltspaceVR was acquired by Microsoft in 2017 and officially shut down on March 10, 2023. Microsoft decided to focus resources on Microsoft Mesh, a business-oriented mixed-reality platform, and discontinued AltspaceVR as part of broader layoffs."
    },
    {
      question: "Is there a free alternative to AltspaceVR for virtual study sessions?",
      answer: "Yes! Chill Study Work offers free virtual study rooms where you can study alongside others with video, audio, and chat. While it's browser-based (no VR headset needed), it provides the same sense of community and shared presence that AltspaceVR users loved."
    },
    {
      question: "Can I host events on CSW like I did on AltspaceVR?",
      answer: "CSW focuses on study sessions rather than entertainment events, but you can create and host your own study rooms, invite friends, and build a community around focused work and learning—similar to the educational gatherings on AltspaceVR."
    },
    {
      question: "How does CSW compare to AltspaceVR?",
      answer: "CSW offers a similar sense of community and shared presence but focuses specifically on productivity. Instead of VR headsets, you access it through any web browser. Features include video study rooms, Pomodoro timers, task management, and ambient sounds."
    },
    {
      question: "Does CSW work on all devices like AltspaceVR did?",
      answer: "Yes! CSW is a web-based platform that works on any device with a browser—Windows, Mac, iOS, Android, tablets, and more. No special hardware or VR headset required."
    },
    {
      question: "Can I find study partners on CSW like I found friends on AltspaceVR?",
      answer: "Absolutely! CSW has public study rooms where you can meet and study alongside people from around the world. Many users form lasting study partnerships and accountability groups, similar to the friendships formed on AltspaceVR."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "AltspaceVR Alternative - Free Virtual Study Rooms",
    "description": "Looking for an AltspaceVR alternative? AltspaceVR shut down in 2023 but CSW offers free virtual study rooms with video, chat, and community features.",
    "url": "https://chillstudy.work/altspacevr/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "about": {
      "@type": "SoftwareApplication",
      "name": "AltspaceVR",
      "applicationCategory": "SocialApplication"
    },
    "mentions": [
      {
        "@type": "SoftwareApplication",
        "name": "Microsoft Mesh",
        "url": "https://www.microsoft.com/en-us/mesh"
      },
      {
        "@type": "SoftwareApplication",
        "name": "VRChat",
        "url": "https://hello.vrchat.com/"
      }
    ],
    "keywords": "AltspaceVR alternative, AltspaceVR replacement, virtual study rooms, online study community, social VR alternative, Microsoft Mesh alternative"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Chill Study Work - AltspaceVR Alternative",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Virtual Study Rooms",
      "Video and Audio Chat",
      "Community Features",
      "Pomodoro Study Timer",
      "Ambient Study Sounds",
      "Cross-Platform Access",
      "Public and Private Rooms",
      "Focus Mode"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>AltspaceVR Alternative - Free Virtual Study Rooms | CSW</title>
        <meta name="description" content="Looking for an AltspaceVR alternative? AltspaceVR shut down in 2023 but CSW offers free virtual study rooms with video, chat, and community features." />
        <meta name="keywords" content="AltspaceVR alternative, AltspaceVR replacement, virtual study rooms, online study community, social VR alternative" />
        <link rel="canonical" href="https://chillstudy.work/altspacevr/" />
        <meta property="og:title" content="AltspaceVR Alternative - Free Virtual Study Rooms" />
        <meta property="og:description" content="AltspaceVR shut down in 2023. Discover CSW's free virtual study rooms with community features." />
        <meta property="og:url" content="https://chillstudy.work/altspacevr/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Clock className="w-4 h-4" />
              AltspaceVR shut down on March 10, 2023
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              The Best AltspaceVR Alternative for Virtual Study Communities
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Miss AltspaceVR's sense of community and shared presence? Chill Study Work offers virtual{" "}
              <Link to="/study-with-me/" className="text-primary hover:underline">study rooms</Link> where 
              you can connect with others, stay accountable, and focus together — all from your browser.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="text-lg px-8">
                <Link to="/altspacevr/history/">
                  Read AltspaceVR's Story
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Shutdown Notice */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
            <h2 className="text-2xl font-bold mb-4">What Happened to AltspaceVR?</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <a href="https://en.wikipedia.org/wiki/AltspaceVR" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">AltspaceVR</a> was 
              a pioneering social VR platform that launched in 2015. Users gathered as avatars in virtual venues to attend comedy shows, 
              educational sessions, meditation groups, and more.{" "}
              <a href="https://news.microsoft.com/2017/10/03/microsoft-acquires-altspacevr/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft acquired it in 2017</a>.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              In January 2023, Microsoft announced AltspaceVR would close on March 10, 2023, as the company shifted focus to{" "}
              <a href="https://www.microsoft.com/en-us/mesh" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Microsoft Mesh</a> for 
              enterprise collaboration.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              If you're looking for a community-focused alternative that doesn't require VR hardware, 
              CSW offers <Link to="/group-study/" className="text-primary hover:underline">virtual study rooms</Link>,{" "}
              <Link to="/study-timer/" className="text-primary hover:underline">study timers</Link>,{" "}
              <Link to="/study-with-friends/" className="text-primary hover:underline">collaboration features</Link>, and{" "}
              <Link to="/online-workspace/" className="text-primary hover:underline">a supportive online workspace</Link>.
            </p>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              AltspaceVR's Community Spirit, Now Browser-Based
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                  <Video className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Virtual Study Rooms</h3>
                <p className="text-muted-foreground">
                  Join live video study rooms and feel the presence of others working alongside you.
                  <Link to="/group-study/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-accent/10 rounded-lg w-fit mb-4">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Community Features</h3>
                <p className="text-muted-foreground">
                  Connect with study partners, join public rooms, and build accountability groups.
                  <Link to="/study-with-strangers/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-green-500/10 rounded-lg w-fit mb-4">
                  <Clock className="w-6 h-6 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Pomodoro Timer</h3>
                <p className="text-muted-foreground">
                  Built-in study timer with Pomodoro technique to structure your focused work sessions.
                  <Link to="/study-timer/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-purple-500/10 rounded-lg w-fit mb-4">
                  <Globe className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">No Hardware Required</h3>
                <p className="text-muted-foreground">
                  Access from any browser—no VR headset needed. Works on desktop, tablet, and mobile.
                  <Link to="/study-with-me/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-orange-500/10 rounded-lg w-fit mb-4">
                  <Sparkles className="w-6 h-6 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Ambient Sounds</h3>
                <p className="text-muted-foreground">
                  Create your ideal study atmosphere with lo-fi beats, rain sounds, and café ambiance.
                  <Link to="/studystream/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border/50">
                <div className="p-3 bg-blue-500/10 rounded-lg w-fit mb-4">
                  <CheckCircle2 className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Task Management</h3>
                <p className="text-muted-foreground">
                  Organize your study goals and track progress with built-in task management.
                  <Link to="/task-management/" className="text-primary hover:underline ml-1">Learn more</Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Other Alternatives */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Other AltspaceVR Alternatives</h2>
            <p className="text-muted-foreground mb-6">
              While CSW focuses on productivity and study, here are other platforms where former AltspaceVR users have found new communities:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <a href="https://www.meta.com/horizon-worlds/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Horizon Worlds</p>
                  <p className="text-sm text-muted-foreground">Meta's social VR platform for events and meetups</p>
                </div>
              </a>
              <a href="https://hello.vrchat.com/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">VRChat</p>
                  <p className="text-sm text-muted-foreground">Highly customizable social VR with user-created worlds</p>
                </div>
              </a>
              <a href="https://spatial.io/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Spatial</p>
                  <p className="text-sm text-muted-foreground">Professional events and collaboration in 3D</p>
                </div>
              </a>
              <a href="https://engagevr.io/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <ExternalLink className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-semibold">Engage</p>
                  <p className="text-sm text-muted-foreground">VR platform for education and corporate training</p>
                </div>
              </a>
            </div>
          </div>
        </section>

        {/* Blog Links */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">Study & Productivity Resources</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/blog/science-behind-study-with-me/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">The Science Behind Study With Me</p>
                <p className="text-sm text-muted-foreground">Why studying with others boosts focus and motivation</p>
              </Link>
              <Link to="/blog/how-turning-on-my-camera-boosted-my-productivity/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">How Turning On My Camera Boosted Productivity</p>
                <p className="text-sm text-muted-foreground">The power of social accountability in virtual spaces</p>
              </Link>
              <Link to="/blog/benefits-of-online-learning-community/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Benefits of Online Learning Communities</p>
                <p className="text-sm text-muted-foreground">How community drives better learning outcomes</p>
              </Link>
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors">
                <p className="font-semibold mb-1">Top 10 Digital Tools for Academic Success</p>
                <p className="text-sm text-muted-foreground">Essential tools every student needs</p>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 bg-primary/10 rounded-lg">
                <HelpCircle className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Frequently Asked Questions
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-card rounded-xl border border-border/50 px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 text-center border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Ready to Join a Productive Community?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Experience the shared presence and community that made AltspaceVR special — now optimized for focused study and work.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                Start Studying Together
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AltspaceVR;
